import { formatJapanPostalCode, normalizeJapanPhone } from "@/utils/japanAddress";

function normalizeValue(value) {
  if (value === null || typeof value === "undefined") return "";
  return `${value}`.trim();
}

function pickValue(source = {}, keys = []) {
  for (const key of keys) {
    const value = normalizeValue(source[key]);
    if (value) return value;
  }
  return "";
}

function pickList(value) {
  return Array.isArray(value) ? value : [];
}

function pickFirstList(candidate = {}) {
  const listKeys = ["dataList", "list", "records", "rows", "items"];

  for (const key of listKeys) {
    const list = pickList(candidate?.[key]);
    if (list.length) return list;
  }

  return [];
}

export function extractCustomerCreditCardInfoList(response = {}) {
  const payload = response?.data ?? response;
  const nestedPayload = payload?.data ?? {};
  const candidates = [
    payload,
    nestedPayload,
  ];

  for (const candidate of candidates) {
    if (Array.isArray(candidate)) return candidate;

    const list = pickFirstList(candidate);
    if (list.length) return list;
  }

  const looksLikePagePayload = payload
    && typeof payload === "object"
    && ("pageNo" in payload || "pageSize" in payload || "totalRecord" in payload || "total" in payload);

  if (payload && typeof payload === "object" && Object.keys(payload).length && !looksLikePagePayload) {
    return [payload];
  }

  return [];
}

export function pickCustomerCreditCardInfo(response = {}) {
  return extractCustomerCreditCardInfoList(response)[0] || null;
}

export function normalizeCustomerCreditCardBillingAddress(info = {}) {
  const firstName = pickValue(info, ["firstName", "first_name", "givenName", "given_name"]);
  const lastName = pickValue(info, ["lastName", "last_name", "familyName", "family_name"]);
  const userRealname = pickValue(info, [
    "userRealname",
    "realname",
    "realName",
    "userName",
    "customerName",
    "cardholderName",
    "cardHolderName",
    "holderName",
    "holder_name",
    "name",
  ]) || [lastName, firstName].filter(Boolean).join(" ");
  const countryCode = pickValue(info, ["countryCode", "country_code", "country"]) || "JP";
  const country = countryCode.toUpperCase() === "JP"
    ? "日本"
    : (pickValue(info, ["countryName", "country_name", "country"]) || countryCode);
  const district = pickValue(info, ["district", "ward", "town"]);
  const street = pickValue(info, ["street", "streetAddress", "street_address"]);
  const detailAddress = pickValue(info, ["detailAddress", "detail_address", "addressDetail", "address_detail"]);
  const addressLine1 = pickValue(info, [
    "addressLine1",
    "address_line1",
    "address1",
    "line1",
  ]) || [district, street].filter(Boolean).join(" ") || detailAddress;
  const addressLine2 = pickValue(info, [
    "addressLine2",
    "address_line2",
    "address2",
    "line2",
  ]) || (detailAddress && detailAddress !== addressLine1 ? detailAddress : "");

  return {
    userRealname,
    email: pickValue(info, ["email", "userEmail", "user_email", "customerEmail", "customer_email"]),
    phone: normalizeJapanPhone(pickValue(info, [
      "phone",
      "userMobile",
      "user_mobile",
      "mobileNo",
      "mobile_no",
      "customerMobile",
      "customer_mobile",
      "tel",
    ])),
    countryCode: countryCode.toUpperCase(),
    country,
    province: pickValue(info, ["province", "state", "prefecture"]),
    city: pickValue(info, ["city", "cityName", "city_name"]),
    addressLine1,
    addressLine2,
    postalCode: formatJapanPostalCode(pickValue(info, [
      "postalCode",
      "postal_code",
      "zipCode",
      "zip_code",
      "zipcode",
    ])),
    birthDate: pickValue(info, ["birthDate", "birth_date"]),
  };
}

export function mergeCustomerCreditCardBillingAddress(target = {}, info = {}, options = {}) {
  const overwrite = options.overwrite !== false;
  const mapped = normalizeCustomerCreditCardBillingAddress(info);

  Object.entries(mapped).forEach(([field, value]) => {
    if (!Object.prototype.hasOwnProperty.call(target, field)) return;
    if (!value) return;
    if (!overwrite && normalizeValue(target[field])) return;
    target[field] = value;
  });

  return mapped;
}
